import { FC, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { InfoCircleOutlined } from '@ant-design/icons'
import Select from '@pages/Admin/CRM/components/base/Select/Select'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import {
  DEFAULT_SIGNING_STATE_OPTIONS,
  mapContractStatusHistory,
  getSigningStateColor,
} from '../../utils/contractSigningState.utils'
import {
  ContractReportFilterOption,
  IContractStatusHistoryItem,
} from '../../utils/types'
import cn from './index.module.css'

interface ISigningStateCell {
  signingState?: string | null
  signingStateLabel?: string | null
  signingTypeLabel?: string | null
  contractStatusHistory?: IContractStatusHistoryItem[] | null
  signedDetails?: string | null
  options?: ContractReportFilterOption[]
  dropdownPlacement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight'
  closeOnScroll?: boolean
  loading?: boolean
  compact?: boolean
  /** Фиксированная ширина поля селекта; длинный текст обрезается, дропдаун — полный */
  fixedSelectWidth?: boolean
  onChange: (signingState: string) => void
}

const SigningStateCell: FC<ISigningStateCell> = ({
  signingState,
  signingStateLabel,
  signingTypeLabel,
  contractStatusHistory,
  signedDetails,
  options = DEFAULT_SIGNING_STATE_OPTIONS,
  dropdownPlacement = 'bottomLeft',
  closeOnScroll = false,
  loading = false,
  compact = false,
  fixedSelectWidth = false,
  onChange,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    if (!closeOnScroll || !dropdownOpen) {
      return undefined
    }

    const closeDropdown = () => setDropdownOpen(false)

    document.addEventListener('scroll', closeDropdown, true)

    return () => {
      document.removeEventListener('scroll', closeDropdown, true)
    }
  }, [closeOnScroll, dropdownOpen])
  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        label: option.label || option.value,
        value: option.value,
        color: getSigningStateColor(option.value),
      })),
    [options],
  )

  const historyItems = mapContractStatusHistory(contractStatusHistory)

  const historyContent =
    historyItems.length > 0 ? (
      <div className={cn.historyTooltip}>
        {historyItems.map((entry, index) => (
          <p
            className={cn.historyRow}
            key={entry.id ?? `${entry.dateLabel}-${entry.statusLabel}-${index}`}
          >
            <span className={cn.historyDate}>{entry.dateLabel}</span>
            <span className={cn.historySeparator}> — </span>
            <span className={cn.historyStatus}>{entry.statusLabel}</span>
          </p>
        ))}
      </div>
    ) : (
      'История изменений пуста'
    )

  return (
    <div
      className={classNames(
        cn.cell,
        compact && cn.cellCompact,
        fixedSelectWidth && cn.cellFixedSelect,
      )}
    >
      <div className={cn.wrapper}>
        <TooltipWrapper
          title={
            fixedSelectWidth || compact
              ? signingStateLabel ?? undefined
              : undefined
          }
        >
          <div className={cn.selectWrap}>
            <Select
              className={iclasses.newSelectCompact}
              popupClassName={cn.statusSelectPopup}
              value={signingState ?? undefined}
              options={selectOptions}
              showOptionColors
              placeholder={signingStateLabel ?? '—'}
              placement={dropdownPlacement}
              dropdownFitContentWidth
              getPopupContainer={() => document.body}
              loading={loading}
              disabled={loading}
              open={closeOnScroll ? dropdownOpen : undefined}
              onDropdownVisibleChange={
                closeOnScroll ? setDropdownOpen : undefined
              }
              onChange={(value) => {
                if (value) {
                  onChange(String(value))
                }
              }}
            />
          </div>
        </TooltipWrapper>
        <TooltipWrapper
          title={historyContent}
          placement="top"
          overlayClassName={cn.historyOverlay}
        >
          <button
            type="button"
            className={cn.infoButton}
            disabled={loading}
            aria-label="История статуса"
          >
            <InfoCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </button>
        </TooltipWrapper>
      </div>
      {!compact && signingTypeLabel && signingTypeLabel !== '—' && (
        <div className={cn.signingType}>{signingTypeLabel}</div>
      )}
      {signedDetails &&
        (fixedSelectWidth || compact ? (
          <TooltipWrapper title={signedDetails}>
            <div
              className={classNames(
                cn.signedDetails,
                cn.signedDetailsTruncated,
              )}
            >
              {signedDetails}
            </div>
          </TooltipWrapper>
        ) : (
          <div className={cn.signedDetails}>{signedDetails}</div>
        ))}
    </div>
  )
}

export default SigningStateCell
